import React, { useState } from "react";
import { Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { verdictUi } from "@/lib/photoRelevance";
import { TabletPhotoCapture } from "@/components/tablet/TabletPhotoCapture";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

function Shell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-4 overflow-auto" data-testid={testid}>
      <div className="w-full max-w-lg bg-slate-900 rounded-3xl p-6 border border-slate-700 my-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-slate-400"><X size={22} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Cleaning completion: same rules as the normal Cleaning screen (required photo, advisory relevance
// check, explicit acknowledgement for a clearly irrelevant photo), using the PIN-verified session.
export function TabletCleaningComplete({ task, client, siteId, onClose, onDone }) {
  const [photo, setPhoto] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [checking, setChecking] = useState(false);
  const [acked, setAcked] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = React.useRef(0);

  const onUploaded = async (fid) => {
    seq.current += 1; const s = seq.current;
    setPhoto(fid); setVerdict(null); setAcked(false);
    if (!fid) { setChecking(false); return; }
    setChecking(true);
    let v = "UNAVAILABLE";
    try {
      const { data } = await client.post("/ai/photo-relevance", { file_id: fid, task_title: task.title, task_description: task.details || null, site_id: siteId });
      v = data?.verdict || "UNAVAILABLE";
    } catch { v = "UNAVAILABLE"; }
    if (s !== seq.current) return;
    setVerdict(v); setChecking(false);
  };
  const needsAck = verdict === "CLEARLY_IRRELEVANT";
  const ok = (!task.photo_required || !!photo) && !checking && !busy && (!needsAck || acked);
  const submit = async () => {
    setBusy(true);
    try {
      await client.post(`/cleaning/instances/${task.id}/complete`, { photo_file_id: photo, relevance_verdict: verdict || undefined, relevance_acknowledged: needsAck ? acked : undefined });
      toast.success("Cleaning completed"); onDone();
    } catch (e) { toast.error(errMsg(e, "Could not complete — the task is still open")); setBusy(false); }
  };
  const ui = verdict ? verdictUi(verdict) : null;
  return (
    <Shell title={task.title} onClose={onClose} testid="tablet-clean-complete">
      {task.details && <p className="text-sm text-slate-400 mb-4">{task.details}</p>}
      {task.photo_required
        ? <TabletPhotoCapture client={client} onUploaded={onUploaded} required />
        : <p className="text-sm text-slate-400" data-testid="tablet-photo-not-required">No photo needed for this task.</p>}
      {checking && <p className="mt-3 text-sm text-slate-400 flex items-center gap-2" data-testid="tablet-relevance-checking"><Loader2 size={14} className="animate-spin" /> Checking photo…</p>}
      {!checking && ui && <p className="mt-3 text-sm flex items-center gap-2 text-slate-300" data-testid={`tablet-relevance-${verdict}`}><Sparkles size={14} /> {ui.label}</p>}
      {needsAck && (
        <label className="mt-3 flex items-center gap-2 text-sm text-red-200">
          <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="tablet-relevance-ack" className="w-5 h-5 accent-brand" />
          I've checked the photo and want to complete anyway
        </label>
      )}
      <div className="flex gap-3 mt-6">
        <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-slate-800" data-testid="tablet-clean-cancel">Cancel</button>
        <button onClick={submit} disabled={!ok} data-testid="tablet-clean-submit" className="flex-1 py-3 rounded-xl bg-brand text-white font-semibold disabled:opacity-40">
          {busy ? "Saving…" : needsAck ? "Complete anyway" : "Mark complete"}
        </button>
      </div>
    </Shell>
  );
}

const YES_NO = new Set(["yes_no", "boolean", "pass_fail", "check", "checkbox"]);

export function TabletChecklist({ task, client, onClose, onDone }) {
  const [rows, setRows] = useState(task.responses || []);
  const [text, setText] = useState({});
  const [busy, setBusy] = useState(null);
  const answer = async (it, response) => {
    if (response === "" || response == null) return;
    setBusy(it.item_id);
    try {
      const { data } = await client.post(`/checklists/instances/${task.id}/respond`, { item_id: it.item_id, response });
      setRows(data.responses || []);
      if (data.status === "completed") { toast.success("Checklist completed"); onDone(); }
    } catch (e) { toast.error(errMsg(e, "Could not save the answer")); }
    finally { setBusy(null); }
  };
  return (
    <Shell title={task.title} onClose={onDone} testid="tablet-checklist">
      <div className="space-y-3 max-h-[60vh] overflow-auto">
        {rows.map((it, i) => (
          <div key={it.item_id} className="rounded-xl bg-slate-800 p-3" data-testid={`tablet-checklist-item-${i}`}>
            <p className="font-medium">{it.title}{it.required ? " *" : ""}</p>
            {it.response != null && <p className="text-xs text-emerald-300 mt-1">Answered: {String(it.response)}</p>}
            {YES_NO.has(it.response_type) ? (
              <div className="flex gap-2 mt-2">
                {[["Yes", true], ["No", false]].map(([l, v]) => (
                  <button key={l} disabled={busy === it.item_id} onClick={() => answer(it, v)} data-testid={`tablet-checklist-${i}-${l.toLowerCase()}`} className="flex-1 py-2 rounded-lg bg-slate-700 active:bg-brand/30">{l}</button>
                ))}
              </div>
            ) : (
              <div className="flex gap-2 mt-2">
                <input value={text[it.item_id] ?? ""} onChange={(e) => setText({ ...text, [it.item_id]: e.target.value })} data-testid={`tablet-checklist-${i}-input`} className="flex-1 px-3 py-2 rounded-lg bg-slate-900 text-white" />
                <button disabled={busy === it.item_id} onClick={() => answer(it, text[it.item_id])} data-testid={`tablet-checklist-${i}-save`} className="px-4 rounded-lg bg-brand text-white">Save</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <button onClick={onDone} className="w-full mt-5 py-3 rounded-xl bg-slate-800" data-testid="tablet-checklist-done">Close</button>
    </Shell>
  );
}
